import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { take } from 'rxjs';
import { languageSet } from '../language';
import { LandingpageService } from '../services/landingpage.service';
import { Survey } from '../dto/survey.dto';
import { SurveySubmission } from '../dto/survey-submission.dto';
import Swal from 'sweetalert2';
import { SurveySubmissionEntry } from '../dto/survey-submission-entry.dto';
import { SurveyQuestion } from '../dto/survey-question.dto';

@Component({
    selector: 'app-show-survey',
    templateUrl: './show-survey.component.html',
    styleUrls: ['./show-survey.component.scss'],
    standalone: false
})
export class ShowSurveyComponent implements OnInit {

  survey: Survey | undefined;
  loadedSurveySubmissions: SurveySubmission[] | undefined;

  myLanguage = languageSet.de;
  browserLocale = window.navigator.language;
  userData: any;
  error = '';
  constructor(
    private route: ActivatedRoute,
    public landingpageService: LandingpageService,
    public oidcSecurityService: OidcSecurityService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    const routeParams = this.route.snapshot.paramMap;
    const surveyId = routeParams.get('surveyId');
    if (surveyId !== null) {
      document.querySelectorAll<HTMLElement>('.efs-global-spinner')[0].style.visibility = 'visible';
      this.oidcSecurityService.isAuthenticated$.pipe(take(1)).subscribe({
        next: (isAuthenticated) => {
          if (isAuthenticated) {
            this.oidcSecurityService.userData$.pipe(take(1)).subscribe({
              next: (userData) => {
                this.userData = userData.userData;
                if (this.userData.preferredLanguage && this.userData.preferredLanguage !== '' && this.userData.preferredLanguage !== 'german') {
                  switch (this.userData.preferredLanguage) {
                    case 'hungarian':
                      this.myLanguage = languageSet.hu;
                      this.browserLocale = 'hu';
                      break;

                    case 'austrian':
                      this.myLanguage = languageSet.at;
                      this.browserLocale = 'de';
                      break;
                  }
                } else {
                  if (!this.userData.nickname.includes('@')) {
                    switch (this.userData.nickname.indexOf(0)) {
                      case 'H':
                        this.myLanguage = languageSet.hu;
                        this.browserLocale = 'hu';
                        break;

                      case 'U':
                        this.myLanguage = languageSet.hu;
                        this.browserLocale = 'hu';
                        break;
                    }
                  }
                }

                this.landingpageService.getSurvey(surveyId).subscribe({
                  next: (survey) => {

                    this.loadedSurveySubmissions = [{
                      survey: survey,
                      answers: survey.questions.map((question: any) => ({
                        question: question,
                        value: question.valueType === 'range' ? '0' : '',
                        values: [],
                      })),
                    }];

                    this.survey = survey;
                    // hide spinner
                    document.querySelectorAll<HTMLElement>('.efs-global-spinner')[0].style.visibility = 'hidden';
                  },
                  error: (err) => {
                    // hide spinner
                    document.querySelectorAll<HTMLElement>('.efs-global-spinner')[0].style.visibility = 'hidden';

                    if (err.error.error && err.error.error === 'Forbidden') {
                      this.error = this.myLanguage.survey_read_missing_permissions;
                    } else {
                      this.error = this.myLanguage.generic_loading_error;
                    }
                  }
                });
              },
              error: (err) => {
                console.error(err);
              }
            });
          }
        },
        error: (err) => {
          console.error(err);
        }
      });
    }
  }

  async submitSurvey(submission: SurveySubmission) {

    // Validate the submission

    // Reset the validation errors
    let hasErrors = false;
    submission.answers.forEach((answer: SurveySubmissionEntry) => {
      answer.validationError = undefined;
    });

    await this.asyncForEach(submission.answers, async (answer: SurveySubmissionEntry) => {
      if (
        // Validate questions without dependencies
        (answer.question?.required && answer.value === '' && answer.values.length === 0 && !answer.question?.isDependencyQuestion) ||

        // Validate questions with dependencies
        (answer.question?.required && answer.value === '' && answer.values.length === 0 && answer.question?.isDependencyQuestion && answer.question?.showDependencyQuestion)
      
      ) {
        answer.validationError = 'Dieses Feld ist erforderlich';
        hasErrors = true;
      }

      if (answer.question?.required && answer.question.valueType === 'range' && (answer.value === '' || answer.value === '0' || (answer.value as any) === 0) && (!answer.question?.isDependencyQuestion || (answer.question?.isDependencyQuestion && answer.question?.showDependencyQuestion))) {
        answer.validationError = 'Bitte wähle, durch anklicken, mindestens einen Stern aus.';
        hasErrors = true;
      }
    });

    submission.hasIssues = hasErrors;

    if (hasErrors) {
      return;
    }

    const clonedSubmission = JSON.parse(JSON.stringify(submission));

    // Filter out answers that are not shown because of dependencies
    clonedSubmission.answers = clonedSubmission.answers.filter((answer: SurveySubmissionEntry) => {
      if (answer.question?.isDependencyQuestion && !answer.question?.showDependencyQuestion) {
        return false;
      }
      return true;
    });

    this.landingpageService.submitSurvey(clonedSubmission.survey?._id!, clonedSubmission).subscribe({
      next: () => {

        Swal.fire({
          title: 'Umfrage abgegeben',
          html: `Vielen Dank für deine Teilnahme! Deine Antworten wurden erfolgreich gespeichert.`,
          icon: 'success',
        });
      },
      error: (error) => {

        if (error && error.error && error.error.description === 'You are not allowed to submit this survey multiple times.') {
          Swal.fire({
            title: 'Fehler',
            html: `Du hast diese Umfrage bereits abgegeben.`,
            icon: 'error',
          });
        } else {
          Swal.fire({
            title: 'Fehler',
            html: `Beim Abschicken der Umfrage ist ein Fehler aufgetreten. Bitte versuche es in einigen Minuten erneut.<br><br>Wende dich bei Fragen an den <a href="https://support.efs-ag.com" target="_blank">EFS-IT Support</a>.`,
            footer: `${error.error.error || 'Unbekannter Fehler'} - ${error.error.message || error.error.description || 'n/a'}`,
            icon: 'error',
          });
        }
      }
    });
  }

  checkDependency(question: SurveySubmissionEntry) {
    const dependencyQuestions = this.survey?.questions.filter((q: SurveyQuestion) => q.dependencyQuestionId === question.question?.uniqueId);

    if (!dependencyQuestions || dependencyQuestions.length === 0) {
      return;
    }

    dependencyQuestions.forEach((dependencyQuestion: SurveyQuestion) => {
      if (dependencyQuestion.dependencyMode === 'select') {
        if (question.values.includes(dependencyQuestion.dependencyValue as string)) {
          dependencyQuestion.showDependencyQuestion = true;
        } else {
          dependencyQuestion.showDependencyQuestion = false;
        }
      }

      if (dependencyQuestion.dependencyMode === 'select-multiple-some') {
        const shouldBeChecked = dependencyQuestion.dependencyValue as string[];

        if (shouldBeChecked.some(option => question.values.includes(option))) {
          dependencyQuestion.showDependencyQuestion = true;
        } else {
          dependencyQuestion.showDependencyQuestion = false;
        }
      }

      if (dependencyQuestion.dependencyMode === 'select-multiple-all') {
        const shouldBeChecked = dependencyQuestion.dependencyValue as string[];

        if (shouldBeChecked.every(option => question.values.includes(option))) {
          dependencyQuestion.showDependencyQuestion = true;
        } else {
          dependencyQuestion.showDependencyQuestion = false;
        }
      }
    });
  }

  isChecked(option: string, values: string[]): boolean {
    return values.includes(option);
  }

  updateCheck(option: string, answer: SurveySubmissionEntry, type: 'checkbox' | 'radio') {
    if (!answer.values) {
      answer.values = [];
    }

    if (type === 'radio') {
      answer.values = [option];
    } else {
      if (answer.values.includes(option)) {
        answer.values = answer.values.filter(value => value !== option);
      } else {
        answer.values.push(option);
      }
    }

    if (answer.question?.valueType === 'select' || answer.question?.valueType === 'radio' || answer.question?.valueType === 'checkbox') {
      this.checkDependency(answer);
    }
  }

  safeHTML(unsafe: string): any {
    return this.sanitizer.bypassSecurityTrustHtml(unsafe);
  }

  async asyncForEach(array: any, callback: any): Promise<void> {
    for (let index = 0; index < array.length; index++) {
      await callback(array[index], index, array);
    }
  }

  renderBusiness(business: string[]): string {
    return business.join(', ')
      .replace('gisaInvestAdvisor', 'Vermögensberater')
      .replace('gisaInsuranceAgent', 'Versicherungsagent / §34d')
      .replace('gisaTipster', 'Tippgeber')
      .replace('gisa34f', '§34f')
      .replace('gisa34i', '§34i')
      .replace('akquisitionsvertragVom', 'Akq.-Vertrag');
  }


}
